import { useEffect, useMemo, useRef, useState } from "react";
import { SERVER_URL } from "../../../constants";

export function useAdultDisplayList(adultIds = [], token, enabled = true) {
  const ids = useMemo(
    () => (Array.isArray(adultIds) ? adultIds.filter(Boolean) : []),
    [adultIds]
  );

  const cacheRef = useRef(new Map());
  const [adults, setAdults] = useState([]);
  const [loading, setLoading] = useState(false);

  const invalidate = (idsToClear = []) => {
    idsToClear.forEach((id) => cacheRef.current.delete(id));
    setAdults((prev) => prev.filter((a) => !idsToClear.includes(a.id)));
  };

  useEffect(() => {
    if (!enabled) return;

    if (!ids.length) {
      setAdults([]);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const missing = ids.filter((id) => !cacheRef.current.has(id));

        if (missing.length) {
          const fetched = [];
          for (const id of missing) {
            try {
              const res = await fetch(`${SERVER_URL}api/adult/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (res.ok) {
                const a = await res.json();
                fetched.push({ ...a, id });
              }
            } catch {
              // fall through to placeholder
            }
          }

          const byId = new Map(fetched.map((a) => [a.id, a]));

          for (const id of missing) {
            const a = byId.get(id);
            const first =
              a?.adultName ?? a?.adult_name ?? a?.firstname ?? a?.first_name ?? "";
            const last =
              a?.adultSurname ?? a?.adult_surname ?? a?.lastname ?? a?.last_name ?? "";
            const name = `${first} ${last}`.trim() || `Adult ${id}`;
            cacheRef.current.set(id, { id, name });
          }
        }

        const out = ids.map((id) => cacheRef.current.get(id));
        if (!cancelled) setAdults(out);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ids, token, enabled]);

  return { adults, loading, invalidate };
}
